import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

import { api } from './client';

export type CouponKind = 'percent' | 'fixed' | 'plan_grant';

export interface AdminCoupon {
  id: string;
  code: string;
  kind: CouponKind;
  percentOff: number | null;
  fixedOffCents: number | null;
  planGrantCode: string | null;
  planGrantDurationDays: number | null;
  appliesToPlans: string[];
  appliesToIntervals: string[];
  maxRedemptions: number | null;
  redemptionCount: number;
  validFrom: string;
  validUntil: string | null;
  firstTimeCustomerOnly: boolean;
  isActive: boolean;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListCouponsQuery {
  limit?: number;
  offset?: number;
  search?: string;
  kind?: CouponKind;
  isActive?: boolean;
}

function buildQS(q: ListCouponsQuery): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export function couponsKey(query: ListCouponsQuery = {}): QueryKey {
  return ['admin', 'coupons', query];
}

export function useAdminCoupons(query: ListCouponsQuery) {
  return useQuery({
    queryKey: couponsKey(query),
    queryFn: () => api<{ total: number; items: AdminCoupon[] }>(`/api/admin/coupons${buildQS(query)}`),
    placeholderData: (prev) => prev,
  });
}

export interface CreateCouponInput {
  code: string;
  kind: CouponKind;
  percentOff?: number;
  fixedOffCents?: number;
  planGrantCode?: string;
  planGrantDurationDays?: number;
  appliesToPlans?: string[];
  appliesToIntervals?: Array<'month' | 'year'>;
  maxRedemptions?: number;
  validFrom?: string;
  validUntil?: string;
  firstTimeCustomerOnly?: boolean;
  notes?: string;
  reason: string;
}

export function useCreateCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCouponInput) =>
      api<AdminCoupon>('/api/admin/coupons', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'coupons'] });
    },
  });
}

export function useSoftDeleteCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<void>(`/api/admin/coupons/${id}`, { method: 'DELETE', body: { reason } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'coupons'] });
    },
  });
}

/** Single-coupon fetch for the detail page.  Server audit-logs the read. */
export function useAdminCoupon(couponId: string | null | undefined) {
  return useQuery({
    queryKey: ['admin', 'coupon', couponId],
    queryFn: () => api<AdminCoupon>(`/api/admin/coupons/${couponId}`),
    enabled: !!couponId,
    staleTime: 10_000,
  });
}

export interface CouponRedemption {
  id: string;
  couponId: string;
  subscriptionId: string | null;
  customerType: 'user' | 'team';
  customerId: string;
  redeemedByUserId: string;
  amountOffCents: number;
  redeemedAt: string;
}

/**
 * Redemption history for a coupon — backs the redemptions table on the detail
 * page.  Backend caps at 200 rows; this is enough for Phase 1 — pagination
 * deferred until a single promo reliably blows past that cap.
 */
export function useCouponRedemptions(couponId: string | null | undefined) {
  return useQuery({
    queryKey: ['admin', 'coupon', couponId, 'redemptions'],
    queryFn: () => api<CouponRedemption[]>(`/api/admin/coupons/${couponId}/redemptions`),
    enabled: !!couponId,
    staleTime: 10_000,
  });
}

export interface PatchCouponInput {
  isActive?: boolean;
  maxRedemptions?: number | null;
  validUntil?: string | null;
  notes?: string;
  reason: string;
}

export function usePatchCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PatchCouponInput }) =>
      api<AdminCoupon>(`/api/admin/coupons/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: (coupon) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'coupons'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'coupon', coupon.id] });
    },
  });
}
